import {
  Check,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Trash2,
} from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import type { ForceExpandState } from '@/hooks/use-force-expand-state'
import { useForceExpandState } from '@/hooks/use-force-expand-state'
import { cn } from '@/lib/utils'

// =============================================================================
// Types
// =============================================================================

export interface RecipeSummary {
  id: string
  name: string
  slug: string
  shortDescription: string | null
  languageSummary: {
    python?: boolean
    javascript?: boolean
  }
}

interface RecipeBlockEditorProps {
  /** Currently selected recipe ID */
  recipeId: string | null
  /** All available recipes */
  allRecipes: RecipeSummary[]
  /** Callback when recipe selection changes */
  onChange: (recipeId: string | null) => void
  /** Callback to delete this block */
  onDelete: () => void
  /** Drag handle props for reordering */
  dragHandleProps?: Record<string, unknown>
  /** Force expand state */
  forceExpandState?: ForceExpandState | null
}

// =============================================================================
// Component
// =============================================================================

export function RecipeBlockEditor({
  recipeId,
  allRecipes,
  onChange,
  onDelete,
  dragHandleProps,
  forceExpandState,
}: RecipeBlockEditorProps) {
  const [isExpanded, setIsExpanded] = useForceExpandState(
    forceExpandState,
    false,
  )
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const selectedRecipe = useMemo(() => {
    if (!recipeId) return null
    return allRecipes.find((r) => r.id === recipeId) || null
  }, [recipeId, allRecipes])

  const filteredRecipes = useMemo(() => {
    if (!searchValue.trim()) {
      return allRecipes
    }
    const query = searchValue.toLowerCase()
    return allRecipes.filter(
      (r) =>
        r.name.toLowerCase().includes(query) ||
        r.slug.toLowerCase().includes(query) ||
        r.shortDescription?.toLowerCase().includes(query),
    )
  }, [allRecipes, searchValue])

  const handleSelect = useCallback(
    (recipe: RecipeSummary) => {
      onChange(recipe.id)
      setSearchValue('')
      setOpen(false)
    },
    [onChange],
  )

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange(null)
    },
    [onChange],
  )

  return (
    <div className="border-border rounded-lg border">
      {/* Header */}
      <div
        className="border-border/50 flex cursor-pointer items-center gap-2 border-b px-3 py-2"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {dragHandleProps && (
          <button
            type="button"
            className="cursor-grab touch-none active:cursor-grabbing"
            {...dragHandleProps}
            aria-label="Drag to reorder"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="text-muted-foreground h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground p-0.5 transition-colors"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-xs font-medium uppercase tracking-wide text-transparent">
          Recipe
        </span>
        {!isExpanded && selectedRecipe && (
          <span className="text-muted-foreground/70 ml-2 truncate text-xs italic">
            {selectedRecipe.name}
          </span>
        )}
        {!isExpanded && !selectedRecipe && (
          <span className="text-muted-foreground/50 ml-2 truncate text-xs italic">
            No recipe selected
          </span>
        )}
        <div className="flex-1" />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
          className="text-muted-foreground hover:text-destructive p-1 transition-colors"
          aria-label="Delete block"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-3">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  'border-input bg-background flex h-9 w-full items-center justify-between rounded-md border px-3 text-sm transition-colors',
                  'hover:bg-muted',
                  !selectedRecipe && 'text-muted-foreground',
                )}
              >
                {selectedRecipe ? (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{selectedRecipe.name}</span>
                    {/* Validation status indicators */}
                    <div className="flex items-center gap-1">
                      {selectedRecipe.languageSummary.python && (
                        <span
                          className="flex items-center gap-0.5 rounded bg-green-500/10 px-1.5 py-0.5 text-[10px] text-green-600"
                          title="Python validated"
                        >
                          <Check className="h-2.5 w-2.5" />
                          py
                        </span>
                      )}
                      {selectedRecipe.languageSummary.javascript && (
                        <span
                          className="flex items-center gap-0.5 rounded bg-green-500/10 px-1.5 py-0.5 text-[10px] text-green-600"
                          title="JavaScript validated"
                        >
                          <Check className="h-2.5 w-2.5" />
                          js
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleClear}
                      className="text-muted-foreground hover:text-destructive ml-auto transition-colors"
                      aria-label="Clear selection"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <span>Select a recipe...</span>
                )}
                <ChevronDown className="text-muted-foreground h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search recipes..."
                  value={searchValue}
                  onValueChange={setSearchValue}
                />
                <CommandList>
                  <CommandEmpty>
                    {allRecipes.length === 0
                      ? 'No recipes available'
                      : 'No matching recipes'}
                  </CommandEmpty>
                  <CommandGroup>
                    {filteredRecipes.map((recipe) => (
                      <CommandItem
                        key={recipe.id}
                        value={recipe.id}
                        onSelect={() => handleSelect(recipe)}
                        className="cursor-pointer"
                      >
                        <div className="flex flex-1 flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{recipe.name}</span>
                            {recipe.id === recipeId && (
                              <Check className="text-primary h-3.5 w-3.5" />
                            )}
                          </div>
                          {recipe.shortDescription && (
                            <span className="text-muted-foreground line-clamp-1 text-xs">
                              {recipe.shortDescription}
                            </span>
                          )}
                        </div>
                        {/* Validation badges */}
                        <div className="flex items-center gap-1">
                          {recipe.languageSummary.python && (
                            <span className="rounded bg-green-500/10 px-1 py-0.5 text-[10px] text-green-600">
                              py
                            </span>
                          )}
                          {recipe.languageSummary.javascript && (
                            <span className="rounded bg-green-500/10 px-1 py-0.5 text-[10px] text-green-600">
                              js
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {selectedRecipe?.shortDescription && (
            <p className="text-muted-foreground mt-2 text-sm">
              {selectedRecipe.shortDescription}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
